<template>
    <div class="tables new-updated-design px-0 px-md-0 pt-4 pt-md-0 pb-4">
        <div v-if="isLoading || depositClaimSubmitting" class="loader-overlay">
            <div class="spinner"></div>
        </div>
        <b-card no-body>
            <b-tabs card v-model="activeTab" @input="onTabChange">
                <b-tab :title="actionRequiredTabTitle" lazy></b-tab>
                <b-tab :title="submittedTabTitle" lazy></b-tab>
                <b-tab :title="completedTabTitle" lazy></b-tab>
            </b-tabs>

            <div class="row mt-3">
                <div class="col-lg-12 grid-margin stretch-card properties-table">
                    <div class="container mw-100">
                        <div class="card">
                            <div class="card-header">
                                <h4 class="card-title">{{ currentTabTitle }}</h4>
                            </div>
                            <div class="card-body">
                                <p v-if="activeTab === 0" class="text-muted mb-3">
                                    {{ $t('dashboard.host_deposit_action_note') }}
                                </p>

                                <vue-good-table
                                    :key="'deposit-table-' + activeTab + '-' + tableRefreshKey"
                                    table-class="vgt-table custom-table"
                                    mode="remote"
                                    :columns="columns"
                                    :rows="rows"
                                    :totalRows="totalRecords"
                                    :search-options="{ enabled: true, placeholder: $t('common.search_placeholder') }"
                                    :pagination-options="{
                                        enabled: true,
                                        perPage: serverParams.perPage,
                                        perPageDropdown: [5, 10, 15, 20]
                                    }"
                                    :sort-options="{ enabled: false }"
                                    @on-page-change="onPageChange"
                                    @on-per-page-change="onPerPageChange"
                                    @on-search="onSearch"
                                >
                                    <template slot="table-row" slot-scope="props">
                                        <span v-if="props.column.field === 'guestName'">{{ props.row.guestName }}</span>
                                        <span v-else-if="props.column.field === 'guestEmail'">{{ props.row.guestEmail }}</span>
                                        <span v-else-if="props.column.field === 'propertyType'">
                                            {{ $t('property_detail.' + props.row.propertyType) }}
                                        </span>
                                        <span v-else-if="props.column.field === 'checkOutDate'">{{ props.row.checkOutDate }}</span>
                                        <span v-else-if="props.column.field === 'depositAmount'">{{ props.row.depositAmount }}</span>
                                        <span v-else-if="props.column.field === 'depositStatus'">
                                            <span :class="props.row.depositStatusClass">{{ props.row.depositStatusLabel }}</span>
                                        </span>
                                        <span v-else-if="props.column.field === 'claimWindowEnds'">{{ props.row.claimWindowEnds }}</span>
                                        <span v-else-if="props.column.field === 'claimReason'">{{ props.row.claimReason }}</span>
                                        <span v-else-if="props.column.field === 'claimedAt'">{{ props.row.claimedAt }}</span>
                                        <span v-else-if="props.column.field === 'action'" class="text-nowrap">
                                            <template v-if="activeTab === 0 && props.row.canManage">
                                                <b-button size="sm" variant="success" class="mr-1"
                                                    @click="hostConfirmNoDamage(props.row)">
                                                    {{ $t('payment.host_confirm_no_damage') }}
                                                </b-button>
                                                <b-button size="sm" variant="warning"
                                                    @click="openDepositClaimModal(props.row)">
                                                    {{ $t('payment.host_file_damage_claim') }}
                                                </b-button>
                                            </template>
                                            <span v-else-if="activeTab === 1" class="badge bg-warning text-dark">
                                                {{ $t('dashboard.deposit_under_review') }}
                                            </span>
                                            <span v-else-if="activeTab === 2" class="text-muted">
                                                {{ props.row.resolutionSummary }}
                                            </span>
                                        </span>
                                    </template>
                                    <div v-if="noDataFound && !isLoading" slot="emptystate" class="text-center text-muted">
                                        {{ $t('common.no_data_available') }}
                                    </div>
                                </vue-good-table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-card>

        <b-modal id="host-deposit-claim-modal" ref="depositClaimModal" hide-footer
            :title="$t('payment.host_file_damage_claim')"
            :no-close-on-backdrop="depositClaimSubmitting"
            :no-close-on-esc="depositClaimSubmitting"
            :hide-header-close="depositClaimSubmitting">
            <b-form @submit.prevent="submitDepositClaim">
                <div v-if="depositClaimSubmitting" class="claim-submit-status text-center mb-3">
                    <div class="spinner spinner-inline mx-auto"></div>
                    <p class="text-muted small mb-0 mt-2">{{ $t('payment.processing') }}</p>
                </div>
                <fieldset :disabled="depositClaimSubmitting">
                    <b-form-group :label="$t('payment.damage_claim_reason')">
                        <b-form-input v-model="depositClaim.reason" required maxlength="255" />
                    </b-form-group>
                    <b-form-group :label="$t('payment.damage_claim_description')">
                        <b-form-textarea v-model="depositClaim.description" rows="4" maxlength="2000" />
                    </b-form-group>
                </fieldset>
                <div class="d-flex justify-content-end">
                    <b-button class="mr-2" variant="secondary" :disabled="depositClaimSubmitting"
                        @click="$refs.depositClaimModal.hide()">
                        {{ $t('sweetalert.cancel') }}
                    </b-button>
                    <b-button variant="primary" type="submit" :disabled="depositClaimSubmitting">
                        <span v-if="depositClaimSubmitting" class="spinner spinner-btn mr-2"></span>
                        {{ depositClaimSubmitting ? $t('payment.processing') : $t('common.submit') }}
                    </b-button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import { debounce } from 'lodash';
import Swal from 'sweetalert2';

export default {
    name: 'HostSecurityDeposits',
    data() {
        return {
            activeTab: 0,
            isLoading: false,
            noDataFound: false,
            rows: [],
            totalRecords: 0,
            depositStats: {},
            tableRefreshKey: 0,
            listRequestId: 0,
            serverParams: {
                page: 1,
                perPage: 10,
                search: '',
                view: 'action_required',
            },
            depositClaimPaymentId: null,
            depositClaimSubmitting: false,
            skipNextTabLoad: false,
            depositClaim: {
                reason: '',
                description: '',
            },
        };
    },
    computed: {
        actionRequiredTabTitle() {
            const count = this.depositStats.action_required_count || 0;
            return `${this.$t('dashboard.deposit_action_required')} (${count})`;
        },
        submittedTabTitle() {
            const count = this.depositStats.submitted_count || 0;
            return `${this.$t('dashboard.deposit_submitted_claims')} (${count})`;
        },
        completedTabTitle() {
            const count = this.depositStats.completed_count || 0;
            return `${this.$t('dashboard.deposit_completed')} (${count})`;
        },
        currentTabTitle() {
            const titles = [
                this.$t('dashboard.deposit_action_required'),
                this.$t('dashboard.deposit_submitted_claims'),
                this.$t('dashboard.deposit_completed'),
            ];
            return titles[this.activeTab] || titles[0];
        },
        columns() {
            const base = [
                { label: this.$t('booking.Guest'), field: 'guestName' },
                { label: this.$t('booking.Guest_Email'), field: 'guestEmail' },
                { label: this.$t('properties_list.Property_Type'), field: 'propertyType' },
                { label: this.$t('dashboard.check_out_date'), field: 'checkOutDate' },
                { label: this.$t('dashboard.deposit_amount'), field: 'depositAmount' },
                { label: this.$t('dashboard.deposit_status'), field: 'depositStatus' },
            ];

            if (this.activeTab === 0) {
                base.push({ label: this.$t('dashboard.claim_window_ends'), field: 'claimWindowEnds' });
            } else if (this.activeTab === 1) {
                base.push({ label: this.$t('payment.damage_claim_reason'), field: 'claimReason' });
                base.push({ label: this.$t('dashboard.deposit_claimed_on'), field: 'claimedAt' });
            }

            base.push({ label: this.$t('properties_list.action'), field: 'action' });
            return base;
        },
    },
    mounted() {
        this.loadItems();
    },
    methods: {
        tabViewForIndex(index) {
            return ['action_required', 'submitted', 'completed'][index] || 'action_required';
        },
        onTabChange(index) {
            // v-model already updated activeTab; only sync params + reload
            this.serverParams.page = 1;
            this.serverParams.view = this.tabViewForIndex(index);
            this.rows = [];
            if (this.skipNextTabLoad) {
                this.skipNextTabLoad = false;
                return;
            }
            this.loadItems();
        },
        onPageChange(params) {
            this.serverParams.page = params.currentPage;
            this.loadItems();
        },
        onPerPageChange(params) {
            this.serverParams.perPage = params.currentPerPage;
            this.serverParams.page = 1;
            this.loadItems();
        },
        onSearch: debounce(function (searchQuery) {
            this.serverParams.search = searchQuery;
            this.serverParams.page = 1;
            this.loadItems();
        }, 300),
        applyDepositStats(stats) {
            if (!stats || typeof stats !== 'object') return;
            this.depositStats = {
                action_required_count: Number(stats.action_required_count) || 0,
                submitted_count: Number(stats.submitted_count) || 0,
                completed_count: Number(stats.completed_count) || 0,
                total_count: Number(stats.total_count) || 0,
            };
        },
        bumpStatsAfterClaim() {
            this.depositStats = {
                ...this.depositStats,
                action_required_count: Math.max(0, (Number(this.depositStats.action_required_count) || 0) - 1),
                submitted_count: (Number(this.depositStats.submitted_count) || 0) + 1,
            };
        },
        flattenRow(row) {
            const payment = row.payment || {};
            const currency = payment.payment_currency || row.currency || 'AED';
            return {
                guestName: payment.user?.name || '',
                guestEmail: payment.user?.email || '',
                propertyType: payment.property?.property_type || '',
                checkOutDate: payment.end_date ? moment(payment.end_date).format('DD-MM-YYYY') : '-',
                depositAmount: `${currency} ${row.amount}`,
                depositStatusLabel: this.formatDepositStatus(row.status),
                depositStatusClass: this.depositStatusClass(row.status),
                claimWindowEnds: row.claim_window_ends_at
                    ? moment(row.claim_window_ends_at).format('DD-MM-YYYY HH:mm')
                    : '-',
                claimReason: row.host_claim_reason || '-',
                claimedAt: row.host_claimed_at
                    ? moment(row.host_claimed_at).format('DD-MM-YYYY HH:mm')
                    : '-',
                paymentId: row.payment_id,
                host_claimed_at: row.host_claimed_at,
                host_no_damage_confirmed_at: row.host_no_damage_confirmed_at,
                canManage: this.canHostManageDeposit(row, payment),
                resolutionSummary: this.buildResolutionSummary(row, currency),
            };
        },
        canHostManageDeposit(row, payment) {
            if (!row || row.host_claimed_at || row.host_no_damage_confirmed_at) return false;
            if (!['held', 'awaiting_release'].includes(row.status)) return false;
            const checkInOut = payment.guest_check_in_out || payment.guestCheckInOut || {};
            const isCheckedOut = checkInOut.is_checked_out === 1 || checkInOut.is_checked_out === true;
            if (!isCheckedOut) return false;
            if (row.claim_window_ends_at && moment(row.claim_window_ends_at).isBefore(moment())) {
                return false;
            }
            return true;
        },
        buildResolutionSummary(row, currency) {
            if (row.status === 'resolved') {
                const parts = [];
                if (row.guest_refund_amount) {
                    parts.push(`${this.$t('dashboard.guest_refund')}: ${currency} ${row.guest_refund_amount}`);
                }
                if (row.host_release_amount) {
                    parts.push(`${this.$t('dashboard.host_release')}: ${currency} ${row.host_release_amount}`);
                }
                return parts.length ? parts.join(' | ') : this.formatDepositStatus(row.status);
            }
            return this.formatDepositStatus(row.status);
        },
        loadItems() {
            this.isLoading = true;
            const requestId = ++this.listRequestId;
            const viewAtRequest = this.serverParams.view;
            const params = { ...this.serverParams };

            const onSuccess = (data) => {
                // Always refresh tab counts from the latest server payload
                if (data.stats && requestId === this.listRequestId) {
                    this.applyDepositStats(data.stats);
                }
                // Ignore outdated row payloads (tab / claim race)
                if (requestId !== this.listRequestId || viewAtRequest !== this.serverParams.view) {
                    if (requestId === this.listRequestId) {
                        this.isLoading = false;
                    }
                    return;
                }
                const fetchedRows = data.data || [];
                this.rows = fetchedRows.map((row) => this.flattenRow(row));
                this.totalRecords = data.totalRecords || 0;
                this.noDataFound = this.rows.length === 0;
                this.isLoading = false;
            };

            const onError = () => {
                if (requestId !== this.listRequestId) return;
                this.isLoading = false;
                this.$showNotification({ message: this.$t('errors.failed_load_data') }, 'error');
            };

            API.getHostSecurityDeposits(params, onSuccess, onError);
        },
        refreshDepositList(targetTab = null) {
            this.serverParams.page = 1;
            if (targetTab !== null) {
                this.serverParams.view = this.tabViewForIndex(targetTab);
                this.rows = [];
                this.tableRefreshKey += 1;
                if (this.activeTab !== targetTab) {
                    this.skipNextTabLoad = true;
                    this.activeTab = targetTab;
                }
                // Single reload so tab counts + rows refresh together
                this.loadItems();
                return;
            }
            this.loadItems();
        },
        hostConfirmNoDamage(row) {
            const paymentId = row.paymentId;
            this.isLoading = true;
            API.hostConfirmSecurityDepositNoDamage(paymentId, (data) => {
                if (data.status == 200) {
                    this.isLoading = false;
                    this.rows = this.rows.filter((r) => r.paymentId !== paymentId);
                    this.depositStats = {
                        ...this.depositStats,
                        action_required_count: Math.max(0, (Number(this.depositStats.action_required_count) || 0) - 1),
                        completed_count: (Number(this.depositStats.completed_count) || 0) + 1,
                    };
                    this.$swal({
                        type: 'success',
                        title: 'Success',
                        text: data.message || this.$t('payment.deposit_release_confirmed'),
                        showConfirmButton: false,
                        timer: 2500
                    }).then(() => {
                        this.refreshDepositList(2);
                    });
                    // this.$showNotification({ message: data.message || this.$t('payment.deposit_release_confirmed') }, 'success');
                    // Move to Completed so refund_pending status is visible
                } else {
                    this.isLoading = false;
                    this.$showNotification({ message: data.message || this.$t('errors.failed_load_data') }, 'error');
                }
            }, () => {
                this.isLoading = false;
                this.$showNotification({ message: this.$t('errors.failed_load_data') }, 'error');
            });
        },
        openDepositClaimModal(row) {
            this.depositClaimPaymentId = row.paymentId;
            this.depositClaim = { reason: '', description: '' };
            this.$refs.depositClaimModal.show();
        },
        submitDepositClaim() {
            if (!this.depositClaimPaymentId || !this.depositClaim.reason || this.depositClaimSubmitting) return;
            const claimedPaymentId = this.depositClaimPaymentId;
            this.depositClaimSubmitting = true;
            this.isLoading = true;
            API.hostClaimSecurityDeposit(claimedPaymentId, this.depositClaim, (data) => {
                if (data.status == 200) {
                    // Immediate UI feedback: row gone + tab counts updated
                    this.rows = this.rows.filter((r) => r.paymentId !== claimedPaymentId);
                    this.bumpStatsAfterClaim();
                    this.$refs.depositClaimModal.hide();
                    this.$showNotification({ message: this.$t('payment.deposit_claim_submitted') }, 'success');
                    this.depositClaimPaymentId = null;
                    this.depositClaimSubmitting = false;
                    // Switch to Submitted Claims and re-fetch authoritative counts/rows
                    this.refreshDepositList(1);
                } else {
                    this.depositClaimSubmitting = false;
                    this.isLoading = false;
                    this.$showNotification({ message: data.message || this.$t('errors.failed_load_data') }, 'error');
                }
            }, () => {
                this.depositClaimSubmitting = false;
                this.isLoading = false;
                this.$showNotification({ message: this.$t('errors.failed_load_data') }, 'error');
            });
        },
        formatDepositStatus(status) {
            if (!status) return '-';
            return status.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        },
        depositStatusClass(status) {
            const map = {
                held: 'badge bg-info text-white',
                awaiting_release: 'badge bg-primary text-white',
                host_claimed: 'badge bg-danger text-white',
                refund_pending: 'badge bg-warning text-dark',
                refunded: 'badge bg-secondary text-white',
                resolved: 'badge bg-success text-white',
            };
            return map[status] || 'badge bg-secondary text-white';
        },
    },
};
</script>

<style scoped>
.loader-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #70D3FF;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}

.spinner-inline {
    width: 28px;
    height: 28px;
    border-width: 3px;
}

.spinner-btn {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-width: 2px;
    vertical-align: -2px;
}

.claim-submit-status {
    background: #f7fafc;
    border-radius: 8px;
    padding: 12px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
<style>
.vgt-global-search {
    padding: 6px;
    border-radius: 10px;
    width: 100%;
    max-width: 275px;
    border: 1px solid rgb(220, 223, 230);
    background: #fff;
    margin-bottom: 15px;
}

.vgt-global-search input {
    border: 0 !important;
    outline: none;
    box-shadow: none;
}
</style>
